import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useLocation } from "react-router";
import { Instagram, Youtube, Facebook } from "lucide-react";
import { api } from "@/lib/api";
import { SOCIALS } from "@/lib/social";
import type { Video, VideosResponse } from "@/lib/videos";
import TikTokIcon from "@/components/ui/TikTokIcon";
import { FALLBACK_VIDEOS } from "./concept-content";
import "./ConceptBits.css";

/* Content plumbing shared by the concept homepages (home-blue, home-pattern),
   so each page only owns its look. */

export type VideoCard = { id: string; title: string; tag: string; img: string; pos: string; url: string };

/** Latest videos from the API, or the placeholder set when it isn't reachable. */
export function useVideoCards(limit = 3): VideoCard[] {
  const [videos, setVideos] = useState<Video[] | null>(null);
  useEffect(() => {
    api.get<VideosResponse>(`/api/videos?sort=recent&limit=${limit}`).then(r => setVideos(r.videos)).catch(() => setVideos([]));
  }, [limit]);
  return videos && videos.length
    ? videos.map(v => ({ id: v.id, title: v.title, tag: v.platform, img: v.thumbnailUrl ?? "/chelsea.JPG", pos: "50% 40%", url: v.externalUrl }))
    : FALLBACK_VIDEOS;
}

export const FOLLOW = [
  { s: SOCIALS.instagram, icon: <Instagram size={20} /> },
  { s: SOCIALS.tiktok, icon: <TikTokIcon className="cb-tt" /> },
  { s: SOCIALS.youtube, icon: <Youtube size={20} /> },
  { s: SOCIALS.facebook, icon: <Facebook size={20} /> },
];

/** Footer links; each concept passes its own club page so the previews link to each other. */
export const footerLinks = (club: string) => [
  { label: "About", href: "/about" },
  { label: "Videos", href: "/videos" },
  { label: "Loose Threads Club", href: club },
  { label: "My favorites", href: "/favorites" },
  { label: "Retreats", href: "/retreats" },
  { label: "Journal", href: "/guides" },
];

/** A bare newsletter form; each concept styles it through its own classes. */
export function Signup({ source, className, button, done }: { source: string; className: string; button: ReactNode; done: ReactNode }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    try { await api.post("/api/subscribe", { email, source }); setStatus("done"); }
    catch { setStatus("error"); }
  }
  if (status === "done") return <p className={`${className} is-done`} role="status">{done}</p>;
  return <form className={className} onSubmit={submit}>
    <label className="cb-sr" htmlFor={`${source}-email`}>Email address</label>
    <input id={`${source}-email`} type="email" required autoComplete="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
    <button type="submit" disabled={status === "loading"}>{status === "loading" ? "Sending…" : button}</button>
    {status === "error" && <p className="cb-error" role="alert">That didn’t go through. Try again in a minute?</p>}
  </form>;
}

/** Behavior for a full-screen phone menu: Escape closes it, focus stays
    inside while open and returns to the toggle after, the page behind
    doesn't scroll, and following a link closes it. Each page draws its own. */
export function useMenu() {
  const [open, setOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const { pathname, hash } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname, hash]);
  useEffect(() => {
    if (!open) return;
    const body = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusables = () => [...(panel.current?.querySelectorAll<HTMLElement>("a[href], button") ?? [])];
    requestAnimationFrame(() => focusables()[0]?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key !== "Tab") return;
      const f = focusables(); if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const btn = toggle.current;
    return () => { document.body.style.overflow = body; document.removeEventListener("keydown", onKey); btn?.focus({ preventScroll: true }); };
  }, [open]);
  return { open, setOpen, toggle, panel };
}
